import React, { useCallback, useContext, useState } from 'react';
import { connect } from 'react-redux';

import T from '@prisjakt/ui-translate';
import { AppContext } from '@prisjakt/ui-app-context';
import { colors } from '@prisjakt/ui-base';
import { Radio } from '@prisjakt/ui-inputs';
import Button from '@prisjakt/ui-button';
import Spinner from '@prisjakt/ui-spinner';
import StoreLogo, { VARIANTS as LOGO_VARIANTS } from '@prisjakt/ui-store-logo';

import ChatIcon from 'mdi-react/MessageReplyIcon';

import Modal from '../../../../../components/modal';

import { AppState } from '../../../../../reducers';

import { Pages } from '../../../../../globals/actions/types';
import { User } from '../../../../../types';
import { SubmitData } from '../types';

import * as S from './styles';

type OwnProps = {
  closeModal: () => void;
  error: boolean;
  isOpen: boolean;
  sending: boolean;
  success: boolean;
  submitReport: (e: React.MouseEvent, data: SubmitData) => void;
};

type StateProps = {
  infoPages: Pages;
  me: User;
};

type Props = OwnProps & StateProps;

const MissingShopModal = ({
  closeModal,
  error,
  isOpen,
  sending,
  success,
  infoPages,
  me,
  submitReport,
}: Props) => {
  const { market, t } = useContext(AppContext);
  const [selectedStoreId, setSelectedStoreId] = useState<number>();
  const [optionalLinkInput, setOptionalLinkInput] = useState<string>();
  const submitted = success || error || sending;
  const registerShopInfoPage = infoPages.nodes.find(
    p => market.infoPageMap && p.id === market.infoPageMap.registerShop
  );

  const handleRadioButtonChange = useCallback(
    (storeId: number) => setSelectedStoreId(storeId),
    []
  );

  const handleInputChange = useCallback(
    event => setOptionalLinkInput(event.target.value),
    []
  );

  const handleSubmit = useCallback(
    (e: React.MouseEvent) => {
      if (!selectedStoreId) {
        return false;
      }
      return submitReport(e, { storeId: selectedStoreId, optionalLinkInput });
    },
    [optionalLinkInput, submitReport, selectedStoreId]
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      shouldShowCloseIcon={!submitted}
      width={500}
    >
      <S.Content>
        {submitted ? (
          <S.SubmitMessage>
            {sending && <Spinner isActive position="none" size={30} />}
            {success && (
              <S.Message>
                <S.CheckIcon color={colors.green1} size="44" />
                <S.SubTitle>
                  {t('product', 'missing-shop.thank-you-title')}
                </S.SubTitle>
                <S.Text>{t('product', 'missing-shop.thank-you-text')}</S.Text>
                <S.Divider />
                <S.Text>{t('product', 'missing-shop.help-text')}</S.Text>
                <S.ButtonsWrapper>
                  <S.Button color="primary" onClick={closeModal}>
                    {t('product', 'missing-shop.help-decline')}
                  </S.Button>
                  <S.Button
                    variant="outlined"
                    color="primary"
                    icon={<ChatIcon size={14} />}
                    iconOutlined={<ChatIcon size={14} />}
                    ariaLabel={t('product', 'missing-shop.help-accept')}
                  >
                    {t('product', 'missing-shop.help-accept')}
                  </S.Button>
                </S.ButtonsWrapper>
              </S.Message>
            )}
            {error && (
              <S.Message>
                <S.ErrorIcon color={colors.red1} size="44" />
                {t('feedback', 'dialog.request-error')}
              </S.Message>
            )}
          </S.SubmitMessage>
        ) : (
          <>
            <S.Title>{t('product', 'missing-shop.report-title')}</S.Title>
            <p>{t('product', 'missing-shop.report-sub-title')}</p>

            <S.Steps>
              <S.Step>
                <S.SubTitle>
                  1. {t('product', 'missing-shop.check-feed-title')}
                </S.SubTitle>
                <S.Text>
                  <T
                    domain="product"
                    hereLink={
                      <S.Link
                        to={
                          registerShopInfoPage
                            ? registerShopInfoPage.pathName
                            : '/info/--i10'
                        }
                      >
                        {t('product', 'missing-shop.check-feed-here-text')}
                      </S.Link>
                    }
                  >
                    missing-shop.check-feed-text
                  </T>
                </S.Text>
                <S.SubText>
                  {t('product', 'missing-shop.check-feed-sub-text')}
                </S.SubText>
              </S.Step>
              <S.Step>
                <S.SubTitle>
                  2. {t('product', 'missing-shop.select-shop-title')}
                </S.SubTitle>

                {me?.analyticsAccess?.stores && (
                  <S.StoresList>
                    {me.analyticsAccess.stores.map(
                      store =>
                        store && (
                          <li key={store.id}>
                            <Radio
                              id={store.name}
                              name="missing-shop"
                              onChange={() => handleRadioButtonChange(store.id)}
                              defaultChecked={selectedStoreId === store.id}
                            >
                              <StoreLogo
                                variant={LOGO_VARIANTS.DEFAULT}
                                name={store.name}
                                storeId={store.id}
                                featured
                                hasImage={Boolean(store.hasLogo)}
                              />
                            </Radio>
                          </li>
                        )
                    )}
                  </S.StoresList>
                )}
                <div>
                  <S.SubText>
                    {t('product', 'missing-shop.optional-link-title')}
                  </S.SubText>

                  <S.Input
                    placeholder="https://"
                    value={optionalLinkInput}
                    onChange={handleInputChange}
                  />
                </div>
              </S.Step>
            </S.Steps>
            <Button
              color="primary"
              onClick={handleSubmit}
              disabled={Boolean(!selectedStoreId)}
            >
              {t('product', 'missing-shop.submit')}
            </Button>
          </>
        )}
      </S.Content>
    </Modal>
  );
};

const mapStateToProps = (state: AppState) => ({
  infoPages: state.globals.infoPages,
  me: state.areas.account.me,
});

export default connect(mapStateToProps, {})(MissingShopModal);
